/**
 * Casca MOBILE (Task #31 Fase 2/3 + Fase 1 03/07) — nav inferior entre
 * Resumo · Fluxo · PLD · Mais.
 *
 * Renderiza a página ativa (cada uma com seu próprio header/logout) + uma barra
 * de tabs fixa no rodapé. Estado de rota leve (useState, sem router). Desktop
 * (>=768) NÃO passa por aqui — zero impacto.
 *
 * Fase 1: ATUALIZAR global (invalida os caches module-level dos hooks e
 * REMONTA a página ativa via `key` — os hooks refazem o fetch) e navegação
 * programática (cards tocáveis do Resumo trocam de aba).
 */
import { useCallback, useState } from "react";
import { Activity, Coins, Diamond, LayoutGrid, MoreHorizontal, Wallet } from "lucide-react";
import { invalidateCalendario6dusCache } from "../../hooks/useCalendario6dus";
import { invalidateContasReceberCache } from "../../hooks/useContasReceber";
import { invalidateCurvaPnlCache } from "../../hooks/useCurvaPnl";
import { invalidateDreV2Cache } from "../../hooks/useDreV2";
import { invalidateDv01IpcaCache } from "../../hooks/useDv01Ipca";
import { invalidateFluxoV2Cache } from "../../hooks/useFluxoV2";
import { invalidatePldProjecaoCache } from "../../hooks/usePldProjecao";
import { invalidatePosicaoAbertaCache } from "../../hooks/usePosicaoAberta";
import { invalidateProximo6duCache } from "../../hooks/useProximo6du";
import { invalidateSaldoV2Cache } from "../../hooks/useSaldoV2";
import { Mobile6du } from "./Mobile6du";
import { MobileFluxo } from "./MobileFluxo";
import { MobileMais } from "./MobileMais";
import { MobilePld } from "./MobilePld";
import { MobilePnl } from "./MobilePnl";
import { MobileResumo } from "./MobileResumo";

export type MobileTab = "resumo" | "6du" | "fluxo" | "pld" | "pnl" | "mais";

// #25b: 6DU ganha a tela dedicada e FECHA os "quatro do Veiga" (6du·Fluxo·
// PLD·PnL). Resumo segue como HOME e o secundário (DRE/Despesas/CR/Exposição)
// fica no "Mais" — 6 tabs em 360px = 60px/alvo (toque ≥44px é a ALTURA,
// min-h-11 mantido).
const TABS: { id: MobileTab; label: string; Icon: typeof LayoutGrid }[] = [
  { id: "resumo", label: "Resumo", Icon: LayoutGrid },
  { id: "6du", label: "6DU", Icon: Coins },
  { id: "fluxo", label: "Fluxo", Icon: Wallet },
  { id: "pld", label: "PLD", Icon: Activity },
  { id: "pnl", label: "PnL", Icon: Diamond },
  { id: "mais", label: "Mais", Icon: MoreHorizontal },
];

/** Invalida os caches module-level consumidos pela casca mobile. */
function invalidarCachesMobile(): void {
  invalidateSaldoV2Cache();
  invalidateFluxoV2Cache();
  invalidateProximo6duCache();
  invalidateCalendario6dusCache();
  invalidatePosicaoAbertaCache();
  invalidatePldProjecaoCache();
  invalidateDreV2Cache();
  invalidateContasReceberCache();
  // #25a (PnL mobile): curva-pnl + DV01 têm cache module-level próprio.
  invalidateCurvaPnlCache();
  invalidateDv01IpcaCache();
}

export function MobileShell({ onLogout }: { onLogout: () => void }) {
  const [tab, setTab] = useState<MobileTab>("resumo");
  // Bump remonta a página ativa: hooks re-executam o fetch pós-invalidate.
  const [refreshKey, setRefreshKey] = useState(0);

  const onRefresh = useCallback(() => {
    invalidarCachesMobile();
    setRefreshKey((k) => k + 1);
  }, []);

  const pageProps = { onLogout, onRefresh, onNavigate: setTab } as const;

  return (
    <div className="min-h-screen bg-indra-azul" data-testid="mobile-shell">
      {/* pb-20 garante que o último card não fique atrás da nav fixa (44px+). */}
      <div className="pb-20" key={refreshKey}>
        {tab === "resumo" && <MobileResumo {...pageProps} />}
        {tab === "6du" && <Mobile6du onLogout={onLogout} onRefresh={onRefresh} />}
        {tab === "fluxo" && <MobileFluxo {...pageProps} />}
        {tab === "pld" && <MobilePld onLogout={onLogout} onRefresh={onRefresh} />}
        {tab === "pnl" && <MobilePnl onLogout={onLogout} onRefresh={onRefresh} />}
        {tab === "mais" && <MobileMais onLogout={onLogout} />}
      </div>

      <nav
        className="fixed bottom-0 inset-x-0 z-20 grid grid-cols-6 border-t border-white/10 bg-indra-azul/95 backdrop-blur"
        data-testid="mobile-nav"
      >
        {TABS.map(({ id, label, Icon }) => {
          const active = tab === id;
          return (
            <button
              key={id}
              type="button"
              data-testid={`mobile-nav-${id}`}
              aria-current={active ? "page" : undefined}
              onClick={() => setTab(id)}
              className={`min-h-11 flex flex-col items-center justify-center gap-0.5 py-1.5 text-xs font-semibold transition-colors ${
                active ? "text-indra-turquesa" : "text-white/60 active:text-white"
              }`}
            >
              <Icon size={18} />
              {label}
            </button>
          );
        })}
      </nav>
    </div>
  );
}
